import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";

const HERO_IMG =
  "https://images.unsplash.com/photo-1773525912476-213bff96b8a4?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function FastTshirtPrintingAuckland() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Fast Custom T-Shirt Printing Auckland",
    serviceType: "DTF custom t-shirt printing",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
    areaServed: { "@type": "City", name: "Auckland" },
  };

  return (
    <>
      <SEOHead
        title="Fast T-Shirt Printing Auckland | 72hr DTF"
        description="Fast custom t-shirt printing in Auckland with 72-hour turnaround. DTF prints for events, fundraisers, schools and small groups. Bring your design or we'll create one."
        path="/fast-tshirt-printing-auckland"
        image={HERO_IMG}
        jsonLd={jsonLd}
      />

      <SEOPageLayout
        heroEyebrow="72-hour turnaround"
        heroH1="Fast Custom T-Shirt Printing in Auckland"
        heroSub="DTF custom t-shirt printing in Auckland with 72-hour turnaround. Perfect for events, fundraisers, school cultural festivals and last-minute team kits."
        heroImage={HERO_IMG}
        heroImageAlt="DTF custom t-shirt printing press — Auckland studio"
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Fast T-Shirt Printing Auckland" },
        ]}
      >
        <h2>Need printed tees this week? We've got you</h2>
        <p>
          Sometimes you don't have three weeks. The festival is on Saturday.
          The fundraiser launches Friday. The cultural performance is in two
          days and the choir still doesn't have shirts. That's exactly what
          our <strong>fast t-shirt printing in Auckland</strong> service is
          built for: high-quality custom prints, turned around in as little
          as 72 hours, with no painful minimum-order requirements.
        </p>
        <p>
          We use DTF (Direct-to-Film) printing — a modern technique that
          produces vibrant, full-colour prints with a soft hand feel,
          excellent wash durability, and crisp detail at any size. It's the
          right method for short runs, complex artwork, and jobs where speed
          matters.
        </p>

        <h2>Why DTF beats screen printing for fast jobs</h2>
        <p>
          Screen printing is great for huge orders of single-colour designs
          — but it's slow, expensive on small runs, and limited on detail.
          DTF flips that: there's no screen setup cost, no minimum-order
          pain, and you can print full-colour photographic artwork at the
          same per-shirt cost as a one-colour print. The result is a
          professional-grade print that holds up wash after wash, and we can
          have it on your shirts in 72 hours.
        </p>

        <h2>What we print on</h2>
        <ul>
          <li>Cotton, polyester &amp; tri-blend t-shirts</li>
          <li>Hoodies, crews &amp; long-sleeves</li>
          <li>Polos &amp; team-issue collared shirts</li>
          <li>Tote bags, caps &amp; aprons</li>
          <li>Customer-supplied garments (BYO)</li>
        </ul>

        <h2>Perfect for…</h2>
        <ul>
          <li><strong>Events &amp; festivals</strong> — Pasifika festivals, school galas, church camps</li>
          <li><strong>Fundraisers</strong> — branded merch with a tight margin and tighter deadline</li>
          <li><strong>Cultural performances</strong> — kapa haka groups, Pasifika dance teams</li>
          <li><strong>Corporate &amp; staff</strong> — staff uniforms, conference shirts, team-building events</li>
          <li><strong>Sports tournaments</strong> — supporter tees, warm-up shirts, manager polos</li>
          <li><strong>Family reunions &amp; birthdays</strong> — themed printed tees for the whānau</li>
        </ul>

        <h2>Bring your artwork — or let us design it</h2>
        <p>
          Already have a logo or design ready to go? Send us a high-res file
          (PNG, AI, PDF or SVG) and we'll print it as-is. No artwork yet?
          That's fine too — our in-house designers can put a concept
          together for a small design fee, then print as soon as you
          approve. Most rush design jobs are turned around the same day.
        </p>

        <h2>Pricing &amp; minimums</h2>
        <p>
          DTF custom t-shirt printing in Auckland from Tribal Designz starts
          at competitive small-batch pricing with significant volume
          discounts as quantity increases. There's no hard minimum order,
          but most rush jobs land between 10 and 100 shirts. Pricing is
          transparent and itemised — you'll see the garment cost, the print
          cost and the design cost (if applicable) separately on every
          quote.
        </p>

        <h2>Auckland pickup or NZ-wide delivery</h2>
        <p>
          Auckland clients can collect from our studio (often the fastest
          option for true rush jobs). For everyone else, we ship NZ-wide via
          courier — typically next-day to most metro areas. If your event is
          tomorrow, message us first; we'll tell you exactly what's
          achievable.
        </p>

        <h2>For longer-term team kits</h2>
        <p>
          Need something more durable for a long competition season?
          Sublimated <Link to="/custom-rugby-uniforms-nz">rugby uniforms</Link>{" "}
          and <Link to="/custom-basketball-jerseys-nz">basketball jerseys</Link>{" "}
          are a better fit. For ongoing bulk staff orders, see our{" "}
          <Link to="/bulk-custom-tshirts-nz">bulk custom t-shirts NZ</Link>{" "}
          page. Want to understand the difference between print methods?{" "}
          <Link to="/blog/sublimation-vs-dtf-printing">
            Read sublimation vs DTF printing →
          </Link>
        </p>
      </SEOPageLayout>
    </>
  );
}
